import React, { useEffect, useState } from 'react';
import Loading from '../Loading';
import { ReactComponent as EditIcon } from '../../assets/svg/Save.svg';
import { getAiPrompts } from '../../server/server';
import EditPromptModal from './EditPromptModal';

interface AiPrompt {
	id: string;
	key: string;
	content: string;
	createdAt: string;
	updatedAt: string;
}

function AiPromptsHome() {
	const [prompts, setPrompts] = useState<AiPrompt[]>([]);
	const [loading, setLoading] = useState(true);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedPrompt, setSelectedPrompt] = useState<AiPrompt | null>(null);

	useEffect(() => {
		loadPrompts();
	}, []);

	const loadPrompts = async () => {
		setLoading(true);
		const resp = await getAiPrompts();
		if (resp.status === 200) {
			setPrompts(resp.data);
		}
		setLoading(false);
	};

	const openEditModal = (prompt: AiPrompt) => {
		setSelectedPrompt(prompt);
		setEditModalOpen(true);
	};

	const closeEditModal = () => {
		setEditModalOpen(false);
		setSelectedPrompt(null);
	};

	const PromptRow = (prompt: AiPrompt) => {
		const preview = prompt.content?.length > 120 ? prompt.content.slice(0, 120) + '...' : prompt.content;
		return (
			<div
				key={prompt.id}
				className='my-[5px] flex flex-row items-center gap-5 rounded-[10px] border border-[#F8F8FB] bg-[#F8F8FB] px-[40px] py-[25px]'
			>
				<div className='min-w-[200px]'>
					<span className='inline-block rounded-full bg-primaryViolet px-3 py-1 text-[13px] font-semibold text-brandYellow'>
						{prompt.key}
					</span>
				</div>
				<div className='flex-1'>
					<p className='text-[14px] font-medium text-mudPurple leading-relaxed'>{preview}</p>
				</div>
				<div>
					<button
						className='flex h-[44px] min-w-[100px] flex-row items-center justify-center rounded-full border border-primaryViolet bg-white px-4 text-[15px] font-semibold text-primaryViolet transition hover:bg-primaryViolet hover:text-brandYellow'
						onClick={() => openEditModal(prompt)}
					>
						Edit
					</button>
				</div>
			</div>
		);
	};

	return (
		<div className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className='w-full min-h-fit max-w-[1200px] px-[10px]'>
				<div className='w-full min-h-[calc(100vh-80px)] bg-white flex flex-col px-[40px] py-[30px] rounded-[10px]'>
					<div className='mb-[30px] flex flex-row items-end'>
						<p className='flex-1 text-[40px] leading-[50px] font-bold text-darkViolet'>AI Prompts</p>
					</div>

					<div className='flex flex-row items-center gap-4 mb-[20px] px-[40px] py-[10px] rounded-[10px] bg-[#F8F8FB]'>
						<p className='min-w-[200px] text-[13px] font-semibold text-mudPurple uppercase tracking-wide'>Key</p>
						<p className='flex-1 text-[13px] font-semibold text-mudPurple uppercase tracking-wide'>Content preview</p>
						<p className='min-w-[100px]'></p>
					</div>

					{loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					)}

					{!loading && prompts.length === 0 && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<p className='text-center text-[14px] font-medium leading-5 text-mudPurple'>No prompts found.</p>
						</div>
					)}

					{!loading && prompts.map(PromptRow)}
				</div>
			</div>

			<EditPromptModal
				modalIsOpen={editModalOpen}
				closeModal={closeEditModal}
				prompt={selectedPrompt}
				reloadPrompts={loadPrompts}
			/>
		</div>
	);
}

export default AiPromptsHome;
